import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import { analyticsApi } from '../lib/api'
import type {
  AnalyticsContactsRequest,
  AnalyticsDashboardRequest,
  AnalyticsHighValueRequest,
  MultiProfileHighValueRequest,
  AnalyticsExportRequest,
  AnalyticsCountScopeRequest,
  AnalyticsApplyTagsRequest,
  AnalyticsCreateTurfRequest,
} from '../types/analyticsMap'

export function useAnalyticsContacts(request: AnalyticsContactsRequest | null, enabled: boolean) {
  return useQuery({
    queryKey: ['analytics', 'contacts', request],
    queryFn: () => analyticsApi.getContacts(request!).then(r => r.data),
    enabled: enabled && !!request,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  })
}

export function useAnalyticsDashboard(request: AnalyticsDashboardRequest | null, enabled: boolean) {
  return useQuery({
    queryKey: ['analytics', 'dashboard', request],
    queryFn: () => analyticsApi.getDashboard(request!).then(r => r.data),
    enabled: enabled && !!request,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  })
}

export function useAnalyticsHighValue(request: AnalyticsHighValueRequest | null, enabled: boolean) {
  return useQuery({
    queryKey: ['analytics', 'high-value', request],
    queryFn: () => analyticsApi.getHighValueVoters(request!).then(r => r.data),
    enabled: enabled && !!request?.profileName,
    placeholderData: keepPreviousData,
  })
}

export function useAnalyticsHighValueMulti(request: MultiProfileHighValueRequest | null, enabled: boolean) {
  return useQuery({
    queryKey: ['analytics', 'high-value-multi', request],
    queryFn: () => analyticsApi.getHighValueMulti(request!).then(r => r.data),
    enabled: enabled && !!request && request.profileNames.length > 0,
  })
}

export function useExportToList() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: AnalyticsExportRequest) =>
      analyticsApi.exportToList(data).then(r => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lists'] })
    },
  })
}

export function useListFilters(listId: string | null) {
  return useQuery({
    queryKey: ['analytics', 'list-filters', listId],
    queryFn: () => analyticsApi.getListFilters(listId!).then(r => r.data),
    enabled: !!listId,
  })
}

export function useScoringProfiles() {
  return useQuery({
    queryKey: ['analytics', 'scoring-profiles'],
    queryFn: () => analyticsApi.getScoringProfiles().then(r => r.data),
    staleTime: 5 * 60_000,
  })
}

export function useCountScope() {
  return useMutation({
    mutationFn: (data: AnalyticsCountScopeRequest) =>
      analyticsApi.countScope(data).then(r => r.data),
  })
}

export function useApplyTags() {
  return useMutation({
    mutationFn: (data: AnalyticsApplyTagsRequest) =>
      analyticsApi.applyTags(data).then(r => r.data),
  })
}

export function useCreateTurf() {
  return useMutation({
    mutationFn: (data: AnalyticsCreateTurfRequest) =>
      analyticsApi.createTurf(data).then(r => r.data),
  })
}
